/**
 * Parent PIN hashing via PBKDF2-SHA256 on the Web Crypto API.
 *
 * Why hash a 4-digit PIN at all? Even though the keyspace is only 10,000 and
 * the hash lives in localStorage on the same device, hashing prevents the
 * PIN from being read out of a browser sync, screen-share, or trivial
 * inspection during over-the-shoulder use. We pair it with rate limiting in
 * the UI so the brute-force window matters in practice.
 *
 * 200k iterations is well above the OWASP 2023 floor for PBKDF2-SHA256 and
 * still completes in well under 100ms on a phone.
 */
const ITERATIONS = 200_000;
const SALT_BYTES = 16;
const HASH_BYTES = 32;

export interface HashedPin {
  salt: string; // base64
  hash: string; // base64
  v: 1; // version, in case we change algorithm later
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function pbkdf2(pin: string, saltBytes: Uint8Array): Promise<Uint8Array> {
  const enc = new TextEncoder();
  // Copy into a fresh ArrayBuffer to satisfy DOM lib's BufferSource (which
  // excludes SharedArrayBuffer-backed views in newer @types).
  const pinBytes = new Uint8Array(enc.encode(pin));
  const saltBuf = new Uint8Array(saltBytes).buffer;
  const key = await crypto.subtle.importKey(
    'raw',
    pinBytes.buffer,
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBuf, iterations: ITERATIONS, hash: 'SHA-256' },
    key,
    HASH_BYTES * 8
  );
  return new Uint8Array(bits);
}

export async function hashPin(pin: string): Promise<HashedPin> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await pbkdf2(pin, salt);
  return { salt: bytesToBase64(salt), hash: bytesToBase64(hash), v: 1 };
}

/** Constant-time comparison. */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

export async function verifyPin(pin: string, stored: HashedPin): Promise<boolean> {
  if (!stored || stored.v !== 1) return false;
  const candidate = await pbkdf2(pin, base64ToBytes(stored.salt));
  return timingSafeEqual(candidate, base64ToBytes(stored.hash));
}

/** Type guard: used by the settings store migration. */
export function isHashedPin(v: unknown): v is HashedPin {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as HashedPin).salt === 'string' &&
    typeof (v as HashedPin).hash === 'string' &&
    (v as HashedPin).v === 1
  );
}
